import { useCallback, useEffect, useState } from "react";
import { api } from "../App";

/* useWebPush
 * ----------
 * Encapsulates the W3C Web Push subscription lifecycle:
 *   - reads VAPID public key from the backend
 *   - subscribes / unsubscribes via the browser's pushManager
 *   - syncs the subscription with our backend
 *
 * Returns:
 *   {
 *     supported,        // browser supports Push + Service Worker + Notification
 *     permission,       // 'default' | 'granted' | 'denied'
 *     enabled,          // user has an active subscription on this device
 *     loading,
 *     enable(),         // requests permission + subscribes + saves
 *     disable(),        // unsubscribes locally + tells backend
 *     test(),           // pings backend to send a test push to this user
 *   }
 *
 * NOTE: On iOS, Web Push requires the user to first add the site to their
 * Home Screen (PWA in standalone mode) — Safari does not expose Push in a
 * regular tab. We surface that limitation to the caller via `iosNeedsInstall`.
 */

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true));

const isIos = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export default function useWebPush() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const iosNeedsInstall = isIos() && !isStandalone();

  const [permission, setPermission] = useState(
    supported ? Notification.permission : "default",
  );
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setEnabled(!!sub);
      setPermission(Notification.permission);
    } catch (_e) {
      setEnabled(false);
    }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (cancelled) return;
        setEnabled(!!sub);
        setPermission(Notification.permission);
      } catch (_e) {
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) throw new Error("Notificaciones no soportadas en este navegador.");
    if (iosNeedsInstall) {
      throw new Error(
        'En iPhone primero agrega Rivones a tu pantalla de inicio (toca Compartir → "Agregar a pantalla de inicio") y abre la app desde ahí.',
      );
    }
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error(
          perm === "denied"
            ? "Activa las notificaciones desde la configuración del navegador para continuar."
            : "Permiso de notificaciones no otorgado.",
        );
      }

      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/notifications/push/public-key");
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);

      // If a previous subscription exists with a different key, drop it first.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try {
          await existing.unsubscribe();
        } catch (_e) {
          /* ignore */
        }
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      await api.post("/notifications/push/subscribe", {
        subscription: sub.toJSON(),
      });
      setEnabled(true);
    } finally {
      setLoading(false);
    }
  }, [supported, iosNeedsInstall]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await api.delete("/notifications/push/subscribe", {
            data: { endpoint: sub.endpoint },
          });
        } catch (_e) {
          /* server may already have cleaned it up */
        }
        await sub.unsubscribe();
      }
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const test = useCallback(async () => {
    const res = await api.post("/notifications/push/test");
    return res.data;
  }, []);

  return {
    supported,
    iosNeedsInstall,
    permission,
    enabled,
    loading,
    enable,
    disable,
    test,
    refresh,
  };
}
